import { useState, useEffect } from 'react';
import { ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

const PROJECTS = [
  { url:require('../images/pf1.webp'), label:'WebToolSpark', category:'Web Development' },
  { url:require('../images/pf2.webp'), label:'LetFind', category:'Web Development' },
  { url:require('../images/pf3.3.png'), label:'Mind Sharper', category:'App Development' },
  { url:require('../images/pf4.webp'), label:'Urbancrazy', category:'Web Development' },
  { url:require('../images/pf5.webp'), label:'Diversiply', category:'UI/UX Design' },
  { url:require('../images/pf6.webp'), label:'Larimarmagic', category:'Web Development' },
];

const CATEGORIES = ['All', 'Web Development', 'App Development', 'UI/UX Design'];

export default function Portfolio() {
  useScrollReveal();

  const [activeTab, setActiveTab] = useState('All');
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const filteredProjects = activeTab === 'All' 
    ? PROJECTS 
    : PROJECTS.filter(p => p.category === activeTab);

  // Force reveal on tab change
  useEffect(() => {
    const timer = setTimeout(() => {
      document.querySelectorAll('.img-reveal').forEach(el => el.classList.add('visible'));
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('visible'));
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const prevImg = () => setLightbox(l => ({ ...l, index: (l.index - 1 + filteredProjects.length) % filteredProjects.length }));
  const nextImg = () => setLightbox(l => ({ ...l, index: (l.index + 1) % filteredProjects.length }));
  const closeImg = () => setLightbox({ open: false, index: 0 });

  return (
    <>
      <SEO title="Portfolio | CodeViora" description="Explore our recent projects and success stories." />
      
      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="container-max relative text-center">
          <div className="max-w-3xl mx-auto animate-fade-up">
            <span className="section-label-inv">Our Work</span>
            <h1 className="section-title-inv text-5xl md:text-6xl mt-2">PROJECTS WE'RE PROUD OF.</h1>
            <p className="section-sub-inv text-lg mx-auto">Take a look at some of our recent digital solutions.</p>

          </div>
        </div>
      </section>

      {/* Filter */}
      <div className="bg-pure-black border-b border-white/10 sticky top-16 z-20">
        <div className="container-max">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-none">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setActiveTab(cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-medium transition-colors tracking-wide ${activeTab === cat ? 'bg-white dark:bg-[#111] text-black dark:text-white' : 'text-white/40 hover:text-white hover:bg-white/5 dark:bg-white/5'}`}
                aria-pressed={activeTab === cat}>
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <section className="bg-grid-white py-20 min-h-[50vh]">
        <div className="container-max">
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, i) => (
              <div key={project.label} className="group cursor-pointer" onClick={() => setLightbox({ open: true, index: i })}>
                <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/3] bg-black/5 dark:bg-white/5">
                  <img 
                    src={project.url} 
                    alt={project.label} 
                    className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white dark:bg-[#111] rounded-full flex items-center justify-center text-black dark:text-white transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100 shadow-xl">
                      <ExternalLink size={20} />
                    </div>
                  </div>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-medium text-black dark:text-white font-display mb-1 group-hover:text-[#ff6b2b] transition-colors">{project.label}</h3>
                    <span className="text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">{project.category}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden relative">
            <div className="flex gap-4 overflow-x-auto scrollbar-none snap-x snap-mandatory pb-4" style={{scrollbarWidth:'none'}}>
              {filteredProjects.map((project, i) => (
                <div key={project.label} className="flex-shrink-0 w-[85vw] max-w-[320px] snap-start group cursor-pointer" onClick={() => setLightbox({ open: true, index: i })}>
                  <div className="relative overflow-hidden rounded-2xl mb-4 aspect-[4/3] bg-black/5 dark:bg-white/5">
                    <img 
                      src={project.url} 
                      alt={project.label} 
                      className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-12 h-12 bg-white dark:bg-[#111] rounded-full flex items-center justify-center text-black dark:text-white transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100 shadow-xl">
                        <ExternalLink size={20} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-medium text-black dark:text-white font-display mb-1 group-hover:text-[#ff6b2b] transition-colors">{project.label}</h3>
                      <span className="text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider">{project.category}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {filteredProjects.length > 0 && <p className="text-center text-xs text-black/40 dark:text-white/40 mt-3">← Swipe to see more →</p>}
          </div>

          {filteredProjects.length === 0 && (
            <div className="col-span-full text-center py-20 text-black/40 dark:text-white/40 font-medium">
              No projects found for this category yet.
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(0,0,0,0.95)'}} onClick={closeImg}>
          <button onClick={closeImg} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
          </button>
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm font-medium">{lightbox.index + 1} / {filteredProjects.length}</div>
          <button onClick={e => { e.stopPropagation(); prevImg(); }} className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Previous">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <div className="max-w-4xl max-h-[80vh] mx-16 md:mx-24" onClick={e => e.stopPropagation()}>
            <img src={filteredProjects[lightbox.index].url} alt={filteredProjects[lightbox.index].label} className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl" />
            <p className="text-center text-white/50 text-sm mt-4">{filteredProjects[lightbox.index].label} — {filteredProjects[lightbox.index].category}</p>
          </div>
          <button onClick={e => { e.stopPropagation(); nextImg(); }} className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10" aria-label="Next">
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
      )}
    </>
  );
}
